import React from "react";
import { BaseCard, SVGIcon } from "../baseCard/baseCard.tsx";
import {
  getFontSizes,
  getTitleHeight,
  darkenColor,
} from "../baseCard/BaseCardUtils.ts";
import "./feasibility.css";

interface FeasibilityProps {
  position: string;
  total: number;
  title: string;
  value: number;
  min: number;
  max: number;
  minColor: string;
  maxColor: string;
  middleColor?: string;
  color: string;
  highlight?: boolean;
  disableDetails?: boolean;
  onDetailsClick?: () => void;
}

const Feasibility: React.FC<FeasibilityProps> = ({
  position,
  total,
  title,
  value,
  minColor,
  maxColor,
  middleColor,
  color,
  highlight = false,
  disableDetails = false,
  onDetailsClick,
}) => {
  const fontSizes = getFontSizes(highlight);
  const titleHeight = getTitleHeight(highlight);
  const shapeColor = darkenColor(color);

  const radius = 100;
  const strokeWidth = 13;
  const normalizedRadius = radius - strokeWidth * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDasharray = `${circumference * 0.75} ${circumference}`;
  const valueDasharray = `${circumference * 0.75 * (value * 100)
    } ${circumference}`;

  const gradientId = `feasibility-gradient-${Math.random()
    .toString(36)
    .substr(2, 9)}`;

  const calculatePointerAngle = () => {
    const startAngle = 135;
    const arcDegrees = 270;
    return startAngle + (value) * arcDegrees;
  };

  const pointerAngle = calculatePointerAngle();

  const generateTicks = () => {
    const ticks = [];
    const totalTicks = 20;
    const arcDegrees = 270;
    const startAngle = 135;

    for (let i = 0; i < totalTicks; i++) {
      const angle = startAngle + (i * arcDegrees) / (totalTicks - 1);
      const isLongTick = i % 5 === 0;

      const angleRad = (angle * Math.PI) / 180;
      const innerRadius = normalizedRadius - 18;
      const outerRadius = normalizedRadius - (isLongTick ? 25 : 22);

      const x1 = 110 + Math.cos(angleRad) * innerRadius;
      const y1 = 110 + Math.sin(angleRad) * innerRadius;
      const x2 = 110 + Math.cos(angleRad) * outerRadius;
      const y2 = 110 + Math.sin(angleRad) * outerRadius;

      ticks.push(
        <line
          key={i}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="var(--color-white)"
          strokeWidth="2"
          strokeLinecap="round"
        />
      );
    }

    return ticks;
  };

  const svgPaths = {
    topShape:
      "M20.7637 1.11044e-05C9.71799 1.24175e-05 0.76368 8.95433 0.763673 20L0.763678 97.4131C9.37694 104.498 32.5399 112.037 67.7002 90.1162C103.94 67.5225 112.736 17.7127 114.172 0L20.7637 1.11044e-05Z",
    bottomShape:
      "M206.493 109.598C206.493 120.643 197.539 129.598 186.493 129.598H0.626953C8.79625 105.382 42.9206 56.9502 114.063 56.9502C185.206 56.9502 205.326 19.2817 206.493 0.447266V109.598Z",
  };

  return (
    <BaseCard highlight={highlight} color={color} className="feasibility-card">
      <SVGIcon
        className="baseCard-icon-top-left"
        viewBox="0 0 115 105"
        fillColor={shapeColor}
        path={svgPaths.topShape}
      />

      <SVGIcon
        className="baseCard-icon-bottom-right"
        viewBox="0 0 207 130"
        fillColor={shapeColor}
        path={svgPaths.bottomShape}
      />

      <div className="baseCard-content feasibility-content">
        <div
          className="feasibility-position"
          style={{ fontSize: fontSizes.position }}
        >
          {position} of {total}
        </div>

        <div
          className="feasibility-title"
          style={{
            fontSize: "16px",
            height: "24px",
          }}
        >
          {title}
        </div>

        <div
          className="baseCard-divider feasibility-divider"
          style={{
            width: "calc(100% + 2 * var(--spacing-3))",
          }}
        ></div>

        <div className="feasibility-gauge">
          <svg width="220" height="170" viewBox="0 0 220 170">
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={maxColor} />
                {middleColor && <stop offset="50%" stopColor={middleColor} />}
                <stop offset="100%" stopColor={minColor} />
              </linearGradient>
            </defs>

            <circle
              cx={110}
              cy={110}
              r={normalizedRadius}
              stroke="var(--color-white)"
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={0}
              transform="rotate(135 110 110)"
              strokeLinecap="round"
            />

            {generateTicks()}

            <circle
              cx={110}
              cy={110}
              r={normalizedRadius}
              stroke={`url(#${gradientId})`}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={valueDasharray}
              strokeDashoffset={0}
              transform="rotate(135 110 110)"
              strokeLinecap="round"
            />

            <path
              d={`M 110 110 
                  L ${110 + Math.cos((pointerAngle * Math.PI) / 180 - 0.15) * 12
                } ${110 + Math.sin((pointerAngle * Math.PI) / 180 - 0.15) * 12
                }
                  L ${110 +
                Math.cos((pointerAngle * Math.PI) / 180) *
                (normalizedRadius - 18)
                } ${110 +
                Math.sin((pointerAngle * Math.PI) / 180) *
                (normalizedRadius - 18)
                }
                  L ${110 + Math.cos((pointerAngle * Math.PI) / 180 + 0.15) * 12
                } ${110 + Math.sin((pointerAngle * Math.PI) / 180 + 0.15) * 12
                }
                  Z`}
              fill="var(--color-white)"
            />
          </svg>
        </div>

        <div
          className="feasibility-score"
          style={{ fontSize: fontSizes.score }}
        >
          {value}
        </div>

        <div
          className="feasibility-index-text"
          style={{ fontSize: fontSizes.indexText }}
        >
          Feasibility Index
        </div>

        {!disableDetails && (
          <button
            className="feasibility-details-button"
            onClick={onDetailsClick}
            style={{ fontSize: fontSizes.button }}
          >
            Details
          </button>
        )}
      </div>
    </BaseCard>
  );
};

export default Feasibility;
